// src/pages/About.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { SparklesIcon, UsersIcon, GlobeAltIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import SEO from '../components/SEO';
import Footer from "../components/layout/footer.jsx";

function About() {
    const teamMembers = [
        {
            name: 'Sarah Johnson',
            role: 'CEO & Founder',
            image: '/team/sarah.jpg',
            bio: 'Former VP of Marketing at TechCorp, passionate about democratizing social media marketing.'
        },
        {
            name: 'Mike Chen',
            role: 'CTO',
            image: '/team/mike.jpg',
            bio: 'AI researcher with 10+ years in machine learning and social media algorithms.'
        },
        {
            name: 'Emma Davis',
            role: 'Head of Product',
            image: '/team/emma.jpg',
            bio: 'Product strategist focused on user experience and social media workflows.'
        }
    ];

    const stats = [
        { name: 'Posts Generated', value: '1M+' },
        { name: 'Active Users', value: '50K+' },
        { name: 'Platforms Supported', value: '10+' },
        { name: 'Customer Satisfaction', value: '98%' }
    ];

    return (
        <>
            <SEO
                title="About PostMgr - AI-Powered Social Media Management"
                description="Learn about PostMgr's mission to democratize social media marketing through AI. Meet our team and discover our story."
                url="https://postmgr.com/about"
            />

            <div className="min-h-screen bg-gray-50">
                {/* Navigation */}
                <nav className="bg-white shadow-sm">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex justify-between h-16">
                            <div className="flex items-center">
                                <Link to="/" className="flex items-center">
                                    <img src="/images/Bob_only.svg" alt="PostMgr" className="h-8 w-8" />
                                    <span className="ml-2 text-2xl font-bold text-blue-600">PostMgr</span>
                                </Link>
                            </div>
                            <div className="hidden md:flex items-center space-x-8">
                                <a href="../#features" className="text-gray-700 hover:text-blue-600">Features</a>
                                <a href="../#pricing" className="text-gray-700 hover:text-blue-600">Pricing</a>
                                <Link to="/about" className="text-gray-700 hover:text-blue-600">About</Link>
                                <Link to="/contact" className="text-gray-700 hover:text-blue-600">Contact</Link>
                            </div>
                            <div className="flex items-center space-x-4">
                                <Link to="/login" className="text-gray-700 hover:text-blue-600">
                                    Sign In
                                </Link>
                                <Link
                                    to="/register"
                                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                                >
                                    Get Started
                                </Link>
                            </div>
                        </div>
                    </div>
                </nav>

                {/* Hero Section */}
                <div className="bg-blue-600 text-white py-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <h1 className="text-4xl font-bold mb-4">About PostMgr</h1>
                        <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                            A venture of <a href="https://baraqu.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">Baraqu LLC</a>, we're on a mission to democratize social media marketing through AI, making it accessible for businesses of all sizes.
                        </p>
                    </div>
                </div>

                {/* Baraqu LLC Partnership Section */}
                <div className="bg-white py-12">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-8">
                            <h2 className="text-3xl font-bold text-gray-900 mb-4">
                                Powered by Innovation
                            </h2>
                            <div className="max-w-4xl mx-auto">
                                <p className="text-lg text-gray-600 mb-6">
                                    PostMgr is a venture of <a
                                    href="https://baraqu.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 font-semibold"
                                >
                                    Baraqu LLC
                                </a>, a technology solutions company that architects, engineers, and deploys solutions others can only dream of creating.
                                </p>
                                <p className="text-gray-600">
                                    With Baraqu's expertise in custom technology solutions ranging from XR simulations to complex engineering projects, PostMgr benefits from cutting-edge innovation and technical excellence that powers our AI-driven social media management platform.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Mission Section */}
                <div className="py-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 lg:grid-cols-1 gap-12 items-center">
                            <div>
                                <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Mission</h2>
                                <p className="text-lg text-gray-600 leading-relaxed">
                                    Born from the innovative spirit of <a
                                    href="https://baraqu.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 font-semibold"
                                >
                                    Baraqu LLC
                                </a>, PostMgr emerged as a solution to the growing complexity of social media marketing. Recognizing that businesses of all sizes were struggling with the time-consuming nature of content creation and social media management, our team leveraged advanced AI technology to create a platform that democratizes access to professional-grade marketing tools.
                                </p>
                                <p className="text-lg text-gray-600 leading-relaxed mt-4">
                                    Drawing from Baraqu's proven track record of creating custom solutions to unique problems—from drone systems for conservation to XR simulations for healthcare—PostMgr represents the same innovative approach applied to the digital marketing space. We believe that powerful marketing tools shouldn't be limited to enterprises with massive budgets.
                                </p>
                            </div>

                        </div>
                    </div>
                </div>


                {/* CTA Section */}
                <div className="bg-gray-900 py-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <h2 className="text-3xl font-bold text-white mb-4">Ready to Get Started?</h2>
                        <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                            Join thousands of businesses using PostMgr to automate their social media and grow their audience.
                        </p>
                        <div className="space-x-4">
                            <Link
                                to="/register"
                                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                            >
                                Start Free Trial
                            </Link>
                            <Link
                                to="/contact"
                                className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-300 hover:text-white hover:border-white"
                            >
                                Contact Sales
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
            <Footer/>
        </>
    );
}

export default About;